import { Column } from '@ant-design/plots';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';

import { GetChargingPileHistoryDataOutput as GetHistoryDataOutput } from '@/types/api/chargingPile/getChargingPileHistoryData';
import { ChargingPile } from '@/types/database/chargingPile/chargingPile';

const meters = {
  energy: '度',
  expense: '元',
};

const sumTitle = {
  day: '當日累積',
  week: '當週累積',
  month: '當月累積',
  year: '當年累積',
  total: '總計累積',
};

const getFontSize = () => {
  const { innerWidth, innerHeight } = window;
  let newFontSize: number;
  if (innerWidth > innerHeight) {
    newFontSize = (innerWidth * 2) / 100;
  } else {
    newFontSize = (innerWidth * 4) / 100;
  }
  return newFontSize;
};

const Chart: React.FC<{
  pile: ChargingPile;
  selectedDate: moment.Moment;
  chartType: 'day' | 'week' | 'month' | 'year' | 'total';
  showMeter: 'expense' | 'energy';
}> = ({ pile, selectedDate, chartType, showMeter }) => {
  const [chartData, setChartData] = useState<GetHistoryDataOutput['data']>([]);
  const [usageText, setUsageText] = useState<string>();
  const [fontSize, setFontSize] = useState<number>(getFontSize());

  const getChartData = useCallback(async () => {
    setUsageText(undefined);
    setChartData([]);
    try {
      const res = await axios.get<GetHistoryDataOutput>(
        '/ezcon/api/getChargingPileHistoryData',
        {
          params: {
            id: pile._id,
            timeStamp: selectedDate.valueOf(),
            chartType,
            type: 'chargingPile',
          },
          headers: {
            authorization: window.localStorage.getItem('feathers-jwt'),
          },
        }
      );
      const newChartData = res.data.data;

      const updateData = setTimeout(() => {
        setChartData(newChartData);
        setUsageText(
          `${sumTitle[chartType]}${showMeter === 'energy' ? '度數' : '費用'}：${
            showMeter === 'energy'
              ? res.data.totalEnergy
              : res.data.totalExpense
          }${meters[showMeter]}`
        );
      }, 200);

      return () => {
        clearTimeout(updateData);
      };
    } catch (err) {
      console.error(err);
      return;
    }
  }, [pile._id, selectedDate, chartType, showMeter]);

  useEffect(() => {
    getChartData();
    return () => {
      setChartData([]);
    };
  }, [getChartData]);

  useEffect(() => {
    const updateFontSize = () => {
      setFontSize(getFontSize());
    };
    window.addEventListener('resize', updateFontSize);
    return () => {
      window.removeEventListener('resize', updateFontSize);
    };
  }, []);

  return (
    <>
      {usageText !== undefined && (
        <Typography
          sx={{
            fontWeight: 'bold',
          }}
        >
          {usageText}
        </Typography>
      )}
      <Box
        sx={{
          height: '17rem',
        }}
      >
        {chartData.length > 0 && (
          <Column
            data={chartData}
            isStack
            renderer="svg"
            xField="createdTime"
            xAxis={{
              label: {
                formatter: (text: string) => {
                  const date = moment(Number(text));
                  switch (chartType) {
                    case 'day':
                      return date.format('H');
                    case 'week':
                      return date.format('dd');
                    case 'month':
                      return date.format('D');
                    case 'year':
                      return date.format('M');
                    case 'total':
                      return date.format('YYYY');
                    default:
                      return date.format('H');
                  }
                },
                style: {
                  fontSize,
                },
              },
            }}
            yField={showMeter}
            yAxis={{
              label: {
                formatter: (text: string) => {
                  switch (showMeter) {
                    case 'expense':
                      return `${text}元`;
                    case 'energy':
                      return `${text}度`;
                  }
                },
                style: {
                  fontSize,
                },
              },
            }}
            seriesField="rateType"
            color={({ rateType }) => {
              switch (rateType) {
                default:
                  return '#69B49E';
                case '夏季尖峰':
                  return '#9F5252';
                case '夏季離峰':
                  return '#CC771C';
                case '非夏季尖峰':
                  return '#69B49E';
                case '非夏季離峰':
                  return '#75ADD0';
                case '其他':
                  return '#b7b7b7';
              }
            }}
            tooltip={{
              formatter: (datum: any) => {
                return {
                  name: datum.rateType,
                  value: `${datum[showMeter]}${
                    showMeter === 'expense' ? '元' : '度'
                  }`,
                };
              },
              title: (datum: any) => {
                switch (chartType) {
                  case 'day':
                    return moment(Number(datum)).format('H點');
                  case 'week':
                    return moment(Number(datum)).format('M月D日');
                  case 'month':
                    return moment(Number(datum)).format('M月D日');
                  case 'year':
                    return moment(Number(datum)).format('M月');
                  case 'total':
                    return moment(Number(datum)).format('YYYY年');
                }
              },
              customContent: (title, data) => {
                return (
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {title}
                    </Typography>
                    {data.map((datum) => (
                      <Typography
                        sx={{
                          color: datum.color,
                        }}
                        key={datum.name}
                      >
                        {datum.name}：{datum.value}
                      </Typography>
                    ))}
                  </Box>
                ) as any;
              },
            }}
            legend={false}
          />
        )}
      </Box>
    </>
  );
};

export default React.memo(Chart);
